import { createContext, use, useEffect } from "react";
import { queryPersister } from "../../app/store/queryPersister";
import { localForageStorage } from "../../app/store/storage";
import { queryClient } from "../../common/api/queryClient";
import { TopAgentType } from "../../common/enums";
import { Permission } from "../../common/security/enums";
import { hasAllPermissions, hasAnyPermission } from "../../common/security/utils";
import { clientSearchActions } from "../client/store";
import { isAgentUserAccount } from "../user/utils";
import { accountSwitchChannel, removeAccountData, sessionQueryOptions } from "./session";
import { subscribeToAuth, useSelectedAccount } from "./store";
import type { Session } from "./types";
import { findUserAccount, getRoleUserAccounts, isActiveUserAccount } from "./utils";

export { useAuthToken, useIsAuthenticated, useSelectedAccount } from "./store";

const EMPTY_PERMISSIONS: Permission[] = [];

const SessionContext = createContext<Session | undefined>(undefined);

// ProtectedRoute provides the session it loads and keeps refreshed.
export const SessionProvider = SessionContext.Provider;

/**
 * Only below ProtectedRoute - the content of an imperative modal (`modal.confirm`) renders outside it. Failing there
 * beats treating the user as one without any permission.
 */
export const useSession = () => {
  const session = use(SessionContext);

  if (!session) {
    throw new Error("useSession must be used within a SessionProvider");
  }

  return session;
};

export const useCurrentUser = () => useSession().user;

export const useCurrentAccount = () => findUserAccount(useCurrentUser(), useSelectedAccount());

export const useCurrentAgentAccount = () => {
  const account = useCurrentAccount();

  return isAgentUserAccount(account) ? account : undefined;
};

export const useHasMultipleAccounts = () =>
  getRoleUserAccounts(useCurrentUser(), useSelectedAccount().role).filter(isActiveUserAccount).length > 1;

export const useIsSystemAdmin = () => useCurrentUser().agentUserRole?.systemAdmin;

export const useIsCurrentAgentTypePlus = (): boolean =>
  useCurrentAgentAccount()?.agent.topAgentType === TopAgentType.PLUS;

export const useIsCurrentAgentTypeFingo = () => useCurrentAgentAccount()?.agent.topAgentType === TopAgentType.FINGO;

export const usePermissions = () => useCurrentAccount()?.permissions ?? EMPTY_PERMISSIONS;

export const useHasPermissions = (...permissions: Permission[]): boolean =>
  hasAllPermissions(usePermissions(), permissions);

export const useHasAnyPermission = (...permissions: Permission[]) => hasAnyPermission(usePermissions(), permissions);

/**
 * Mounted once, in App. The store changes the same way for this tab's actions and for those synced from other tabs,
 * so a logout or an account switch clears the query cache in every tab. The tabs that did not switch the account
 * reload for it rather than patch their state live.
 */
export const useSessionSync = () => {
  useEffect(() => {
    // Versions before CLIIP-1459 persisted the session there through redux-persist, remember-me token included.
    // TODO(CLIIP-1459) remove a month after the release - by then the active devices have dropped it.
    localForageStorage.removeItem("persist:root");

    const unsubscribe = subscribeToAuth((state, previous) => {
      if (previous.session && !state.session) {
        queryClient.removeQueries();
        clientSearchActions().sessionEnded();
      } else if (state.selectedAccount.userId !== previous.selectedAccount.userId) {
        // Another user logged in, also in another tab - the session query still holds the previous one and may be
        // refreshing it. The reset drops both and loads the new user.
        removeAccountData();
        queryClient.resetQueries({ queryKey: sessionQueryOptions().queryKey });
      } else if (state.selectedAccount.accountId !== previous.selectedAccount.accountId) {
        removeAccountData();
      }
    });

    // The switching tab's throttled writes cannot reach IndexedDB before the reload, so start from an empty cache.
    const reload = () => Promise.resolve(queryPersister.removeClient()).finally(() => (window.location.href = "/"));
    accountSwitchChannel.addEventListener("message", reload);

    return () => {
      unsubscribe();
      accountSwitchChannel.removeEventListener("message", reload);
    };
  }, []);
};
